import { AppSettingType, FeatureManagementType, Permissions, RebarEstimationStatus } from "@api/apiClient";

import { useAppSettings } from "@api/appSettings";
import { CustomGridTreeDataGroupingCell } from "@components/DataGrid/components/cell/CustomGridTreeDataGroupingCell";
import QuantificationGridHeader from "@components/DataGrid/components/headers/QuantificationGridHeader";
import VersionChip from "@components/VersionChip/VersionChip";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SettingsIcon from "@mui/icons-material/Settings";
import { Switch, Theme, Tooltip } from "@mui/material";
import { GRID_TREE_DATA_GROUPING_FIELD, GridActionsCellItem, GridApi, GridColDef, GridGroupNode, GridRenderCellParams, GridRowParams, GridValidRowModel } from "@mui/x-data-grid-premium";
import AppStore from "@stores/AppStore";
import _ from "lodash";
import { MutableRefObject } from "react";

export interface ActionButtonsProps {
  handleCloneScenario: (row: any) => void;
  handleDeleteScenario: (row: any) => void;
  handleCopyModelMaterials: (row: any) => void;
  handleEditScenario: (row: any) => void;
  handleScenarioSettings: (row: any) => void;
  handleScenarioEnableFinal: (event: React.ChangeEvent<HTMLInputElement>, row: any) => void;
}

const defaultImpactColumns = ["totalCo2"];
const adStageImpactColumns = ['a1A2A3', 'a4A5', 'a', 'b1', 'b2B5', 'biogenic', 'b', 'c', 'd', 'totalCo2'];

export const getColumns = (apiRef: MutableRefObject<GridApi>, appStore: AppStore, theme: Theme, props: ActionButtonsProps, appSettings: ReturnType<typeof useAppSettings>): GridColDef[] => {
  const columns = getDefaultColumns(apiRef, appStore, theme, props, appSettings);

  const impactColumns = appStore.hasFeature(FeatureManagementType.ScenariosADStages) ? adStageImpactColumns : defaultImpactColumns;
  const impactColumnsToInsert = impactColumns.map((col) => getCo2eColumnDef(appStore, col));

  const index = 0;

  columns.splice(index, 0, ...impactColumnsToInsert);

  // Attach action handlers to the isFinalForProjectPhase column


  return columns;
}

export const getDefaultColumns = (
  _apiRef: MutableRefObject<GridApi>,
  _appStore: AppStore,
  _theme: Theme,
  props: ActionButtonsProps,
  appSettings: ReturnType<typeof useAppSettings>,
): GridColDef[] => [
  /*
  {
    field: "key",
    headerName: i18n.t("scenarios-grid.columns.key"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.key")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.key;
    }
  },
  */

  /* A-D Impact Columns will be inserted here ... */
  /* A-D Impact Columns will be inserted here ... */
  /* A-D Impact Columns will be inserted here ... */

  {
    field: "description",
    headerName: i18n.t("scenarios-grid.columns.description"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.description")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row?.data?.description;
    },
  },

  /*
  {
    field: "percentOfEmissionReductionTarget",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.percent-of-emission-reduction-target"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.percent-of-emission-reduction-target")} unit={i18n.t("units.%")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      return row.data.percentOfEmissionReductionTarget;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("percent", { value });
    },
  },
  */
  {
    field: "apsVersion",
    headerName: i18n.t("scenarios-grid.columns.version"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.version")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      return row.data?.model?.apsVersion;
    },
    renderCell: (params) => {
      if (!params.row.data?.model?.apsVersion) {
        return null;
      }
      return <VersionChip label={params.row.data?.model?.modelVersion || ""} />;
    },
  },
  {
    field: "percentRebar",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenario-grid.columns.percent-rebar"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenario-grid.columns.percent-rebar")} unit={i18n.t("units.%")} />,
    flex: 1,
    valueGetter: (_value: any, row: any) => {
      if (row.data?.rebarEstimationStatus === RebarEstimationStatus.Enabled) {
        return i18n.t("scnenario-grid.percent-rebar.enabled");
      }
      if (row.data?.rebarEstimationStatus === RebarEstimationStatus.Estimated) {
        return i18n.t("scnenario-grid.percent-rebar.estimated");
      }
    },
  },
  {
    field: "percentAssigned",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.percent-assigned"), unit: i18n.t("units.%") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.percent-assigned")} unit={i18n.t("units.%")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      return row?.data?.percentAssigned;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("percent", { value });
    },
  },
  ...(appSettings.data?.profileLevelRebarSettings
    ? [
        {
          field: "profile",
          headerName: i18n.t("scenarios-grid.columns.profile"),
          renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.profile")} />,
          flex: 1,
          valueGetter: (_value: any, row: any) => {
            return row?.data?.profile?.name || "";
          },
        },
      ]
    : []),

  {
    field: "fullName",
    //headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.percent-assigned"), unit: i18n.t("units.%") }),
    headerName: i18n.t("scenarios-grid.columns.fullName"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.fullName")} />,
    flex: 1,

    valueGetter: (_value: any, row: any) => {
      return row?.data?.fullName;
    },
  },
  {
    field: "createdAt",
    headerName: i18n.t("scenarios-grid.columns.create-date"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.create-date")} />,
    flex: 1,
    type: "number",
    renderCell: (params: any) => {
      if (params.row.type === "scenario") {
        return i18n.t("datetime_short", { value: new Date(params.row.data.createdAt) });
      }
    },
  },

  ...(_appStore.hasFeature(FeatureManagementType.ProjectPhase)
    ? ([
        {
          field: "projectPhase",
          headerName: i18n.t("scenarios-grid.columns.projectPhase"),
          renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.projectPhase")} />,
          flex: 1,
          valueGetter: (_value: any, row: any) => {
            return row?.data?.projectPhase?.name;
          },
        },
        {
          field: "isFinalForProjectPhase",
          headerName: i18n.t("scenarios-grid.columns.final-scenario"),
          renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.final-scenario")} />,
          type: "boolean" as const,
          valueGetter: (_value: any, row: any) => {
            if (row.type !== "scenario") return null;
            return row.data?.isFinalForProjectPhase ?? false;
          },
          renderCell: (params: GridRenderCellParams) => {
            if (params.row.type !== "scenario") return null;
            const isFinal = params.row.data?.isFinalForProjectPhase ?? false;
            const projectPhaseName = params.row.data?.projectPhase?.name;
            if (!projectPhaseName) return null;
            return (
              <Tooltip title={isFinal ? i18n.t("scenarios-grid.final-scenario-for-phase.enabled", { projectPhase: projectPhaseName }) : i18n.t("scenarios-grid.final-scenario-for-phase.disabled")}>
                <Switch checked={isFinal} onChange={(event) => props.handleScenarioEnableFinal(event, params.row)} color="primary" size="medium" />
              </Tooltip>
            );
          },
          sortable: true,
          filterable: true,
          sortComparator: (v1: boolean, v2: boolean) => {
            if (v1 === v2) return 0;
            return v1 ? 1 : -1;
          },
        },
      ] as GridColDef[])
    : []),
  ...(_appStore.hasFeature(FeatureManagementType.ScenarioType)
    ? ([
        {
          field: "scenarioType",
          headerName: i18n.t("scenarios-grid.columns.scenario-type"),
          renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.scenario-type")} />,
          flex: 1,
          valueGetter: (_value: any, row: any) => {
            if (row.type !== "scenario") return null;
            return row.data?.scenarioTypeEntity?.name ?? row.data?.scenarioTypeId ?? "";
          },
          renderCell: (params: GridRenderCellParams) => {
            if (params.row.type !== "scenario") return null;
            const name = params.row.data?.scenarioTypeEntity?.name;
            return name ?? "—";
          },
        },
      ] as GridColDef[])
    : []),
  {
    field: "actions",
    type: "actions",
    headerName: i18n.t("scenarios-grid.columns.actions"),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.actions")} />,
    resizable: false,
    flex: 1,
    align: "right",
    getActions: (params: GridRowParams) => {
      if (params.row.type === "scenario") {
        return [
          <EditScenarioButton row={params.row} onClick={props.handleEditScenario} />,
          <CloneScenarioButton row={params.row} onClick={props.handleCloneScenario} />,
          <ScenarioSettingsButton row={params.row} onClick={props.handleScenarioSettings} />,
          <DeleteScenarioButton row={params.row} onClick={props.handleDeleteScenario} />,
        ];
      } else if (params.row.type === "model") {
        return [<CopyModelMaterialsButton row={params.row} onClick={props.handleCopyModelMaterials} />];
      } else {
        return [];
      }
    },
  },
];

export const getA1A2A3Co2eColumnDef = (): GridColDef => {
  return   {
    field: "a1A2A3",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.a1A2A3"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.a1A2A3")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const a1a2a3 = row?.data?.a1A2A3;

      if (a1a2a3 == null) {
        return null;
      }

      return a1a2a3 || 0;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getA4A5Co2eColumnDef = (): GridColDef => {
  return   {
    field: "a4A5",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.a4A5"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.a4A5")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const a4 = row?.data?.a4;
      const a5 = row?.data?.a5;

      if (a4 == null && a5 == null) {
        return null;
        
      }
      return (a4 || 0) + (a5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getACo2eColumnDef = (): GridColDef => {
  return   {
    field: "a",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.a"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.a")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const a1a2a3 = row?.data?.a1A2A3;
      const a4 = row?.data?.a4;
      const a5 = row?.data?.a5;

      if (a1a2a3 == null && a4 == null && a5 == null) {
        return null;
      }

      return (a1a2a3 || 0) + (a4 || 0) + (a5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getB1Co2eColumnDef = (): GridColDef => {
  return   {
    field: "b1",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.b1"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.b1")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const b1 = row?.data?.b1;

      if (b1 == null) {
        return null;
      }

      return b1 || 0;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}
export const getB2B5Co2eColumnDef = (): GridColDef => {
  return   {
    field: "b2B5",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.b2B5"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.b2B5")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const b2 = row?.data?.b2;
      const b3 = row?.data?.b3;
      const b4 = row?.data?.b4;
      const b5 = row?.data?.b5;

      if (b2 == null && b3 == null && b4 == null && b5 == null) {
        return null;
      }

      return (b2 || 0) + (b3 || 0) + (b4 || 0) + (b5 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getBiogenicCo2eColumnDef = (): GridColDef => {
  return   {
    field: "biogenic",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.biogenic"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.biogenic")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const biogenic = row?.data?.biogenic;

      if (biogenic == null) {
        return null;
      } 
      
      return biogenic || 0;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getBCo2eColumnDef = (): GridColDef => {
  return   {
    field: "b",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.b"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.b")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const b1 = row?.data?.b1;
      const b2 = row?.data?.b2;
      const b3 = row?.data?.b3;
      const b4 = row?.data?.b4;
      const b5 = row?.data?.b5;
      const b6 = row?.data?.b6;
      const b7 = row?.data?.b7;

      if (b1 == null && b2 == null && b3 == null && b4 == null && b5 == null && b6 == null && b7 == null) {
        return null;
      }

      return (b1 || 0) + (b2 || 0) + (b3 || 0) + (b4 || 0) + (b5 || 0) + (b6 || 0) + (b7 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getCCo2eColumnDef = (): GridColDef => {
return  {
    field: "c",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.c"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.c")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      const c1 = row?.data?.c1;
      const c2 = row?.data?.c2;
      const c3 = row?.data?.c3;
      const c4 = row?.data?.c4;

      if (c1 == null && c2 == null && c3 == null && c4 == null) {
        return null;
      }

      return (c1 || 0) + (c2 || 0) + (c3 || 0) + (c4 || 0);
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getDCo2eColumnDef = (): GridColDef => {
  return   {
    field: "d",
    headerName: i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.d"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={i18n.t("scenarios-grid.columns.d")} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      return row?.data?.d;
    },
    valueFormatter: (value?: number) => {
      if (!_.isFinite(value)) return;
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getTotalCo2ColumnDef = (_appStore: AppStore): GridColDef => {
  const headerName = _appStore.hasFeature(FeatureManagementType.ADStages)
    ? i18n.t("scenarios-grid.columns.totalCo2-A-D")
    : i18n.t("scenarios-grid.columns.totalCo2");
  return   {
    field: "totalCo2",
    headerName: _appStore.hasFeature(FeatureManagementType.ADStages) ? i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.totalCo2-A-D"), unit: i18n.t("units.tCO2e") }) : i18n.t("quantification-header", { name: i18n.t("scenarios-grid.columns.totalCo2"), unit: i18n.t("units.tCO2e") }),
    renderHeader: () => <QuantificationGridHeader name={headerName} unit={i18n.t("units.tCO2e")} />,
    flex: 1,
    type: "number",
    valueGetter: (_value: any, row: any) => {
      return row?.data?.totalCo2;
    },
    valueFormatter: (value?: number) => {
      return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
    },
  }
}

export const getGroupingColumnDef = (headerName: string): GridColDef => {
  return {
    field: "name",
    headerName: headerName,
    renderHeader: () => <QuantificationGridHeader name={headerName} />,
    filterable: true,
    hideable: false,
    renderCell: (params) => {
      return <CustomGridTreeDataGroupingCell {...(params as GridRenderCellParams<any, any, any, GridGroupNode>)} hideDescendantCount={true} />;
    },
    valueGetter: (_value: any, row: any) => row?.data?.name,
  };
};

export const getCo2eColumnDef = (_appStore: AppStore, column: string): GridColDef => {
  switch (column) {
    case "a1A2A3":
      return getA1A2A3Co2eColumnDef();
    case "a4A5":
      return getA4A5Co2eColumnDef();
    case "a":
      return getACo2eColumnDef();
    case "b1":
      return getB1Co2eColumnDef();
    case "b2B5":
      return getB2B5Co2eColumnDef();
    case "biogenic":
      return getBiogenicCo2eColumnDef();
    case "b":
      return getBCo2eColumnDef();
    case "c":
      return getCCo2eColumnDef();
    case "d":
      return getDCo2eColumnDef();
    case "totalCo2":
      return getTotalCo2ColumnDef(_appStore);
  }

  return null as any;
};

const CloneScenarioButton = (props: { row: GridRowParams; onClick: (row: GridValidRowModel) => void }) => {
  const { row, onClick } = props;
  const upStore = useUserPermissionStore();

  return (
    <GridActionsCellItem
      key={`clone-scenario-${row.id}`}
      icon={
        <Tooltip title={i18n.t("scenarios-grid.clone-button.title")}>
          <ContentCopyIcon />
        </Tooltip>
      }
      label={i18n.t("scenarios-grid.clone-button.title")}
      disabled={!upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
      onClick={() => onClick(row)}
    />
  );
};

const DeleteScenarioButton = (props: { row: GridRowParams; onClick: (row: GridValidRowModel) => void }) => {
  const { row, onClick } = props;
  const upStore = useUserPermissionStore();

  return (
    <GridActionsCellItem
      key={`clone-scenario-${row.id}`}
      icon={
        <Tooltip title={i18n.t("scenarios-grid.delete-button.title")}>
          <DeleteIcon />
        </Tooltip>
      }
      label={i18n.t("scenarios-grid.delete-button.title")}
      disabled={!upStore.hasPermission(Permissions.App_Project_Scenario_Delete)}
      onClick={() => onClick(row)}
    />
  );
};

const EditScenarioButton = (props: { row: GridRowParams; onClick: (row: GridValidRowModel) => void }) => {
  const { row, onClick } = props;
  const upStore = useUserPermissionStore();
  return (
    <GridActionsCellItem
      key={`edit-scenario-${row.id}`}
      icon={
        <Tooltip title={i18n.t("scenarios-grid.edit-button.title")}>
          <EditIcon />
        </Tooltip>
      }
      label={i18n.t("scenarios-grid.edit-button.title")}
      disabled={!upStore.hasPermission(Permissions.App_Project_Scenario_Edit)}
      onClick={() => onClick(row)}
    />
  );
};

const ScenarioSettingsButton = (props: { row: GridRowParams; onClick: (row: GridValidRowModel) => void }) => {
  const { row, onClick } = props;
  const upStore = useUserPermissionStore();
  const appSettings = useAppSettings();
  return (
    <GridActionsCellItem
      key={`scenario-settings-${row.id}`}
      icon={
        <Tooltip title={i18n.t("scenarios-grid.settings-button.title")}>
          <SettingsIcon />
        </Tooltip>
      }
      label={i18n.t("scenarios-grid.settings-button.title")}
      disabled={!appSettings.data?.profileLevelRebarSettings && (!appSettings.data?.rebarEstimation || !upStore.hasPermission(Permissions.App_Project_Scenario_Edit))}
      onClick={() => onClick(row)}
    />
  );
};




const CopyModelMaterialsButton = (props: { row: GridRowParams; onClick: (row: GridValidRowModel) => void }) => {
  const { row, onClick } = props;
  const upStore = useUserPermissionStore();

  return (
    <GridActionsCellItem
      key={`clone-scenario-${row.id}`}
      icon={
        <Tooltip title={i18n.t("scenarios-grid.copy-modal-materials-dialog.copy-button.title")}>
          <ContentCopyIcon />
        </Tooltip>
      }
      label={i18n.t("scenarios-grid.copy-modal-materials-dialog.copy-button.title")}
      disabled={!upStore.hasPermission(Permissions.App_Project_Scenario_Create)}
      onClick={() => onClick(row)}
    />
  );
};

const generateColumns = (srcArr: string[], addArr: string[], insertAfter: string): string[] => {
  const newArr = [];
  const insertIndex = srcArr.indexOf(insertAfter);
  if (insertIndex === -1) {
    newArr.push(...srcArr);
    newArr.push(...addArr.map((col) => `${col}`));
  } else {
    for (let i = 0; i < srcArr.length; i++) {
      if (i === insertIndex) {
        newArr.push(srcArr[i]);
        newArr.push(...addArr.map((col) => `${col}`));
      } else {
        newArr.push(srcArr[i]);
      }
    }
  }
  return newArr;
};

export const getFilteredColumns = (columns: GridColDef[], appStore: AppStore): { filteredColumns: GridColDef[]; columnVisibilityModel: Record<string, boolean> } => {
  let filteredColumns = [...columns];
  let defaultVisibleColumns = [
    "__check__",
    GRID_TREE_DATA_GROUPING_FIELD,
    "label", // TODO: this is the tree view?

    // NOTE: A-D inserted here

    "totalCo2",

    "description",
    "apsVersion",
    "percentRebar",
    "percentAssigned",
    "actions",
    "profile",
    "fullName",
    "createdAt",
    "projectPhase",
    "isFinalForProjectPhase", // Final for Phase column
    "scenarioType", // Scenario Type column
  ];

  const impactColumns = appStore.hasFeature(FeatureManagementType.ScenariosADStages) ? adStageImpactColumns : defaultImpactColumns;

  if (appStore.hasFeature(FeatureManagementType.ResultsADStages)) {
    defaultVisibleColumns = generateColumns(defaultVisibleColumns, impactColumns, "tag");
  }

  // Generate column visibility model
  const columnVisibilityModel = filteredColumns.reduce<Record<string, boolean>>((acc, col) => {
    acc[col.field] = defaultVisibleColumns.includes(col.field); // Set true for defaultColumns, false for others
    return acc;
  }, {});
  
  return { filteredColumns, columnVisibilityModel };   
}
